import React from 'react';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Elements } from '@stripe/react-stripe-js';

import { ProcessorConfigs, ProcessorProps } from '../../@types';
import { SupportedProcessors } from '../../constants';
import { stripeProviderFactory } from '../../services/providers/StripeProviderFactory';
import { useCommonOptionsStore } from '../../store';
import { generateDefaultTokenexFormConfig } from '../payment-methods/CreditCard/tokenexFormConfig';
import { createLogLocal } from '../../utils/log-local';

import type { StripeElementLocale } from '@stripe/stripe-js';

type StripeProviderType =
  | ReturnType<(typeof stripeProviderFactory)['createProvider']>
  | Record<string, never>;

type ExtendedProcessorProps = Omit<ProcessorProps, 'method'> & {
  method: ProcessorProps['method'] | 'payment_element';
};

const log = createLogLocal('ProcessorProcessor.tsx');

/**
 * Find the Stripe processor config from the processors array.
 * Returns undefined if no Stripe processor is configured.
 */
const findStripeProcessor = (processors: ProcessorConfigs) =>
  processors.find(({ processor }) => processor === SupportedProcessors.STRIPE);

const getStripePublishableKey = (processors: ProcessorConfigs) =>
  findStripeProcessor(processors)?.stripe?.clientKey;

const getStripeAccount = (processors: ProcessorConfigs) =>
  findStripeProcessor(processors)?.stripe?.account;

const getStripeType = (processors: ProcessorConfigs) =>
  findStripeProcessor(processors)?.stripe?.type;

export const getStripePaymentMethodConfiguration = (
  processors: ProcessorConfigs,
) => {
  const pmc =
    findStripeProcessor(processors)?.stripe?.paymentMethodConfiguration;
  return pmc;
};

/**
 * Wrap child element with a payment processor provider, making the payment
 * processor object available to all child components.
 *
 * Example
 * -------
 * If the payment processor is Stripe, this makes `useStripe` and `useElements`
 * hooks available to children of `<Processor>`, by wrapping them in a
 * fully-configured `<Elements>` component.
 */
export const ProcessorProvider = ({
  method,
  processors,
  methodConfig,
  billingConfig,
  options,
  children,
}: ExtendedProcessorProps) => {
  const locale = useCommonOptionsStore(state => state.lang);
  const stripeTheme = useCommonOptionsStore(state => state.stripeTheme);
  const setOriginalCurrency = useCommonOptionsStore(
    state => state.setOriginalCurrency,
  );
  const setPropertyCurrency = useCommonOptionsStore(
    state => state.setPropertyCurrency,
  );

  // Handle currency conversion for PayPal at the top level with useEffect
  React.useEffect(() => {
    if (methodConfig.processor === SupportedProcessors.PAYPAL) {
      const currency = options.currency?.toUpperCase();
      const propertyCurrency = processors.find(
        item => item.processor === SupportedProcessors.PAYPAL,
      )?.paypal.currency;

      if (propertyCurrency) {
        setOriginalCurrency(currency);
        setPropertyCurrency(propertyCurrency);
      } else {
        setOriginalCurrency(undefined);
        setPropertyCurrency(undefined);
      }
    }
  }, [
    methodConfig.processor,
    options.currency,
    processors,
    setOriginalCurrency,
    setPropertyCurrency,
  ]);

  /**
   * Get the Stripe provider (if Stripe is the selected payment processor).
   */
  const { provider: stripeProvider, getOptions } =
    React.useMemo<StripeProviderType>(
      function getStripeProvider() {
        log('getStripeProvider', 'options:', options);
        if (SupportedProcessors.STRIPE === methodConfig.processor) {
          const publishableKey = getStripePublishableKey(processors);
          return stripeProviderFactory.createProvider({
            publishableKey,
            options: options as any,
            locale: locale as StripeElementLocale,
            paymentMethodConfiguration: methodConfig.paymentMethodConfiguration,
            account: getStripeAccount(processors),
            type: getStripeType(processors),
          });
        }
        return {};
      },
      [processors, methodConfig, options, locale],
    );

  /* --------- STRIPE --------- */
  if (methodConfig.processor === SupportedProcessors.STRIPE) {
    const stripeOptions = getOptions(method);
    if (method === 'payment_element') {
      stripeOptions.appearance = {
        ...stripeTheme.paymentElement,
      };
    }
    return (
      <Elements options={stripeOptions} stripe={stripeProvider}>
        {children}
      </Elements>
    );
  }

  /* ---------- PAYPAL ---------- */
  if (methodConfig.processor === SupportedProcessors.PAYPAL) {
    let currency = options.currency?.toUpperCase();

    const propertyCurrency = processors.find(
      item => item.processor === SupportedProcessors.PAYPAL,
    )?.paypal.currency;

    // Currency conversion is now handled in the useEffect at the top level
    if (propertyCurrency && currency !== propertyCurrency) {
      currency = propertyCurrency;
    }

    const elementOptions = {
      dataPartnerAttributionId: 'DigitalArbitrageInc_Ecom',
      clientId: processors.find(
        item => item.processor === SupportedProcessors.PAYPAL,
      )!.paypal.clientId,
      currency,
      intent: 'capture',
      components: 'buttons',
      enableFunding: ['paylater', 'venmo'],
      disableFunding: ['card'],
    };

    return (
      <PayPalScriptProvider options={elementOptions}>
        {children}
      </PayPalScriptProvider>
    );
  }

  /* ---------- DLOCAL & TOKENEX & PAYU ---------- */
  if (
    methodConfig.processor === SupportedProcessors.DLOCAL ||
    methodConfig.processor === SupportedProcessors.TOKENEX ||
    methodConfig.processor === SupportedProcessors.PAYU ||
    methodConfig.tokenization?.processor === SupportedProcessors.TOKENEX
  ) {
    const tokProcessor = methodConfig?.tokenization?.processor;
    /** @TODO Determine if we should care about this (tokProcessor) */
    if (!tokProcessor) return null;

    // const {
    //   origin,
    //   timestamp,
    //   tokenexId: tokenExID,
    //   cardsSchema: tokenScheme,
    //   cardsAuthenticationKey: authenticationKey,
    //   cvvSchema: tokenSchemeCvv,
    //   cvvAuthenticationKey: authenticationKeyCvv,
    // } = methodConfig.tokenization[tokProcessor];

    // const tokenexConfig = {
    //   card: {
    //     origin,
    //     timestamp,
    //     tokenExID,
    //     tokenScheme,
    //     authenticationKey,
    //   },
    //   cvv: {
    //     origin,
    //     timestamp,
    //     tokenExID,
    //     tokenScheme: tokenSchemeCvv,
    //     authenticationKey: authenticationKeyCvv,
    //   },
    // };

    const formConfig = {
      ...generateDefaultTokenexFormConfig(),
      billingFields: {
        ...billingConfig,
      },
    };

    // TODO: ????
    // we probably need to pass the tokenization config to the children
    // to be able to use the tokenization processor
    // if this passed then children should not query the processor itself
    // but use the passed tokenization config
    // But it is debatable and very unlikely that we will need this
    return (
      <>
        {React.Children.map(children, child => {
          if (React.isValidElement<{ fields: typeof formConfig }>(child)) {
            return React.cloneElement(child, {
              fields: formConfig,
            });
          }
          return child;
        })}
      </>
    );
  }

  /* ---------- OTHER (NO PROCESSOR) ---------- */
  return <>{children}</>;
};
